import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "urql";

import { File } from "../../../components/graphql/schema.graphql";
import { useUrqlResponseHandler } from "../../../hooks";
import { UpdateFile } from "../queries.graphql";

function getStem(filename: string) {
  const parts = filename.split(".");
  if (parts.length <= 1) return filename; // No extension found
  parts.pop(); // Remove the extension
  return parts.join(".");
}

type DriveRenameFileDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  file: Pick<File, "id" | "name">;
  refreshView: () => void;
};

export default function DriveRenameFileDialog({
  isOpen,
  onClose,
  file,
  refreshView,
}: DriveRenameFileDialogProps) {
  const inputRef = useRef<HTMLInputElement>();
  const [newFileName, setNewFileName] = useState("");
  const [nameChanged, setNameChanged] = useState(false);
  const { getResponseFromState } = useUrqlResponseHandler();
  const [, updateFile] = useMutation(UpdateFile);

  useEffect(() => {
    setNewFileName(file?.name || "");
    setNameChanged(false);
  }, [file?.name]);

  useEffect(() => {
    if (newFileName && !nameChanged && inputRef.current) {
      inputRef.current.setSelectionRange(0, getStem(newFileName).length);
    }
  }, [newFileName]);

  const handleDialogClose = () => {
    onClose();
  };

  const handleSubmit = () => {
    void updateFile({
      updateFileInput: {
        fileId: file.id,
        name: newFileName,
      },
    }).then((result) => {
      const { error } = getResponseFromState({
        state: result,
        errorMessage: "Failed to rename file",
      });
      if (!error) {
        onClose();
        setNewFileName("");
        refreshView();
      }
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "400px",
          },
        },
      }}
    >
      <DialogTitle>Rename File</DialogTitle>
      <DialogContent>
        <TextField
          inputRef={inputRef}
          autoFocus
          margin="dense"
          id="name"
          label="File Name"
          type="text"
          value={newFileName}
          onChange={(event) => {
            setNewFileName(event.target.value);
            setNameChanged(true);
          }}
          onKeyDown={(event) => {
            switch (event.key) {
              case "Enter":
                handleSubmit();
                break;
              case "Escape":
                handleDialogClose();
                break;
            }
          }}
          fullWidth
          variant="standard"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} variant="contained">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          Rename
        </Button>
      </DialogActions>
    </Dialog>
  );
}
